import { Grid, Typography } from "@mui/material";
import { useSegments } from "api/segments";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import {
  SelectInput,
  SelectOption,
} from "components/FormControls/Select/Select";
import { TierSetDropdown } from "components/FormControls/TierSetDropdown/TierSetDropdown";
import { useStyles } from "components/Layout/Campaign/Common/Campaign.styles";
import { Field, useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { get } from "lodash";
import { useState } from "react";
import { CampaignVisibilityTarget, VisibilityTarget } from "types/campaigns";
import { SegmentData } from "types/segments";
import { SegmentsFetcher } from "./SegmentsFetcher";

interface VisibilityOptions {
  label: string;
  id: CampaignVisibilityTarget;
}

const visibilityOptions: VisibilityOptions[] = [
  { label: "visibleForEveryone", id: VisibilityTarget.ALL },
  { label: "visibleForSegment", id: VisibilityTarget.SEGMENT },
  { label: "visibleForTier", id: VisibilityTarget.TIER },
  { label: "hidden", id: VisibilityTarget.NONE },
];

export const VisibilitySection = () => {
  const classes = useStyles();
  const { message } = useMessage();
  const { values } = useFormikContext<any>();
  const getFormData = (formField: string) => get(values, formField);
  const [listOfFetchedElements, setListOfFetchedElements] = useState<
    SegmentData[]
  >([]);
  const [searchSegements, setSearchSegments] = useState("");
  const { useInfiniteSegmentsList } = useSegments();

  const {
    data: infiniteSegmentsList,
    size: segmentsSize,
    setSize: setSegmentsSize,
    isValidating,
  } = useInfiniteSegmentsList({
    filters: searchSegements
      ? [
          {
            condition: "like",
            query: "name",
            value: searchSegements,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: segmentsList, anyElementsToFetch: anySegmentsToFetch } =
    useManageInfiniteScroll(infiniteSegmentsList || []);

  const segmentsLabels: SelectOption[] =
    segmentsList?.map(({ name, segmentId }) => ({
      label: name,
      id: segmentId,
    })) || [];

  return (
    <Grid container spacing={12} mt={4}>
      <Field name="visibility.target">
        {({ form }: { form: any }) => (
          <Grid xs={12} item>
            <SelectInput
              width="100%"
              data-test-id="campaign-form-basic-settings-visibility"
              items={visibilityOptions.map(
                (
                  option,
                ): {
                  label: string;
                  id: string;
                } => ({
                  id: option.id,
                  label: message({ id: option.label }),
                }),
              )}
              label={message({
                id: "automationVisibility",
              })}
              name="visibility.target"
              onChange={(e) => {
                const { value } = e.target;
                const { TIER, SEGMENT, NONE, ALL } = VisibilityTarget;

                if (value === TIER) {
                  form.setFieldValue("visibility", {
                    target: TIER,
                    tiers: [],
                  });
                } else if (value === SEGMENT) {
                  form.setFieldValue("visibility", {
                    target: SEGMENT,
                    segments: [],
                  });
                } else if (value === NONE) {
                  form.setFieldValue("visibility", {
                    target: NONE,
                  });
                } else {
                  form.setFieldValue("visibility", {
                    target: ALL,
                  });
                }
              }}
            />
            <Typography className={classes.campaignVisibilityInfo}>
              {message({
                id: "automationVisibilityInfo",
              })}
            </Typography>
          </Grid>
        )}
      </Field>
      <Field name="visibility.segments">
        {({ form }: { form: any }) =>
          form.values.visibility?.target === VisibilityTarget.SEGMENT && (
            <Grid xs={12} item>
              {getFormData("visibility.segments")?.map(
                (item: string, index: number) => (
                  <SegmentsFetcher
                    id={item}
                    key={index}
                    setListOfFetchedSegments={(data) => {
                      setListOfFetchedElements((previousArrayState) => [
                        ...previousArrayState,
                        data,
                      ]);
                    }}
                  />
                ),
              )}
              <SelectInput
                loadMoreElements={() =>
                  !isValidating && setSegmentsSize(segmentsSize + 1)
                }
                anyElementsToFetch={anySegmentsToFetch}
                multiple
                displaySearch
                data-test-id="campaign-form-basic-settings-visibility-targeted-segments"
                items={segmentsLabels}
                label={
                  <RequiredFieldLabel
                    text={message({ id: "targetedSegments" })}
                  />
                }
                name="visibility.segments"
                asyncSearch={(searchedQuery: string) => {
                  setSearchSegments(searchedQuery);
                }}
                initialValue={form.values.visibility?.segments?.map(
                  (value: string) => {
                    const foundSegment =
                      listOfFetchedElements &&
                      listOfFetchedElements?.find(
                        (segment: { segmentId: string }) =>
                          segment.segmentId === value,
                      );
                    return {
                      label: foundSegment?.name || "",
                      id: foundSegment?.segmentId || "",
                    };
                  },
                )}
              />
            </Grid>
          )
        }
      </Field>
      <Field name="visibility.tiers">
        {({ form }: { form: any }) =>
          form.values.visibility?.target === VisibilityTarget.TIER && (
            <Grid xs={12} item>
              <TierSetDropdown
                name="visibility.tiers"
                label={message({ id: "targetedTiers" })}
              />
            </Grid>
          )
        }
      </Field>
    </Grid>
  );
};
